import React, { useEffect, useState, useCallback } from 'react';
import { Card, Typography, Space, Tag, Button, Tooltip, Row, Col } from 'antd';
import { 
  CheckCircleOutlined, 
  CloseCircleOutlined, 
  SyncOutlined, 
  DatabaseOutlined, 
  ApiOutlined, 
  HddOutlined 
} from '@ant-design/icons';
import { dashboardService } from '../../services/dashboardService';
import { useAuth } from '../../contexts/AuthContext';

const { Text } = Typography;

export const SystemHealthCard: React.FC = () => {
  const { currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  const [health, setHealth] = useState<{ status: string; timestamp: string; database: string; redis?: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastCheck, setLastCheck] = useState<string>('');

  const fetchHealth = useCallback(async () => {
    if (!isSuperAdmin) return;
    setLoading(true);
    const data = await dashboardService.getSystemHealth();
    setHealth(data);
    setLastCheck(new Date().toLocaleTimeString('fr-FR'));
    setLoading(false);
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    fetchHealth();
    const interval = setInterval(fetchHealth, 30000);
    return () => clearInterval(interval);
  }, [fetchHealth, isSuperAdmin]);

  if (!isSuperAdmin) {
    return null;
  }

  const isApiOk = health?.status === 'READY';
  const isDbOk = health?.database === 'connected';
  const isRedisOk = health?.redis === 'connected';
  const redisText = isRedisOk 
    ? 'Connecté' 
    : (health?.redis === 'memory_fallback' ? 'Mode Mémoire' : (health?.redis === 'disconnected' ? 'Déconnecté' : 'Actif'));

  return (
    <Card 
      size="small"
      style={{ 
        marginBottom: 16, 
        borderRadius: 12, 
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        border: '1px solid #e2e8f0',
        background: '#ffffff'
      }}
    >
      <Row justify="space-between" align="middle" gutter={[16, 8]}>
        <Col xs={24} sm={16} md={18}>
          <Space size="middle" wrap>
            <Text strong style={{ color: '#0a543d', display: 'flex', alignItems: 'center', gap: 6 }}>
              <ApiOutlined /> État du Système:
            </Text>

            <Tooltip title="Statut de l'API Node.js & Serveur HTTP">
              <Tag 
                icon={isApiOk ? <CheckCircleOutlined /> : <CloseCircleOutlined />} 
                color={isApiOk ? 'success' : 'error'}
                style={{ borderRadius: 12, padding: '2px 10px', fontSize: 12 }}
              >
                API: {isApiOk ? 'Opérationnelle' : 'Inaccessible'}
              </Tag>
            </Tooltip>

            <Tooltip title="Connexion à la base de données PostgreSQL">
              <Tag 
                icon={isDbOk ? <DatabaseOutlined /> : <CloseCircleOutlined />} 
                color={isDbOk ? 'success' : 'error'}
                style={{ borderRadius: 12, padding: '2px 10px', fontSize: 12 }}
              >
                PostgreSQL: {isDbOk ? 'Connectée' : 'Déconnectée'}
              </Tag>
            </Tooltip>

            <Tooltip title="Adaptateur Socket.IO & File d'attente Redis">
              <Tag 
                icon={isRedisOk ? <HddOutlined /> : (health?.redis === 'disconnected' ? <CloseCircleOutlined /> : <HddOutlined />)} 
                color={isRedisOk ? 'success' : (health?.redis === 'memory_fallback' ? 'processing' : 'warning')}
                style={{ borderRadius: 12, padding: '2px 10px', fontSize: 12 }}
              >
                Redis/Socket: {redisText}
              </Tag>
            </Tooltip>
          </Space>
        </Col>

        <Col xs={24} sm={8} md={6} style={{ textAlign: 'right' }}>
          <Space size="small">
            {lastCheck && <Text type="secondary" style={{ fontSize: 11 }}>MàJ {lastCheck}</Text>}
            <Button 
              type="text" 
              size="small" 
              icon={<SyncOutlined spin={loading} />} 
              onClick={fetchHealth}
              title="Actualiser le statut"
            />
          </Space>
        </Col>
      </Row>
    </Card>
  );
};
